import { useCallback, useEffect, useState, memo } from 'react'

// ─────────────────────────────────────────────────────────────────────────────
// HOOK — syncs selected index and scroll snaps with the Embla API.
// Properly cleans up event listeners on unmount to prevent memory leaks.
// ─────────────────────────────────────────────────────────────────────────────
export const useDotButton = (emblaApi) => {
  const [selectedIndex, setSelectedIndex] = useState(0)
  const [scrollSnaps,   setScrollSnaps]   = useState([])

  const onInit = useCallback((api) => {
    setScrollSnaps(api.scrollSnapList())
  }, [])

  const onSelect = useCallback((api) => {
    setSelectedIndex(api.selectedScrollSnap())
  }, [])

  const onDotButtonClick = useCallback((index) => {
    emblaApi?.scrollTo(index)
  }, [emblaApi])

  useEffect(() => {
    if (!emblaApi) return

    onInit(emblaApi)
    onSelect(emblaApi)

    emblaApi
      .on('reInit', onInit)
      .on('reInit', onSelect)
      .on('select',  onSelect)

    // Clean up listeners on unmount — prevents memory leaks
    return () => {
      emblaApi
        .off('reInit', onInit)
        .off('reInit', onSelect)
        .off('select',  onSelect)
    }
  }, [emblaApi, onInit, onSelect])

  return { selectedIndex, scrollSnaps, onDotButtonClick }
}

// ─────────────────────────────────────────────────────────────────────────────
// DOT BUTTON — accessible navigation dot for Embla carousels.
//
// Props:
//   index    {number}  — zero-based position of this dot
//   total    {number}  — total number of slides
//   selected {boolean} — whether this dot is active
//   onClick  {func}    — click handler
//   ...rest            — any extra props forwarded to the <button>
// ─────────────────────────────────────────────────────────────────────────────
export const DotButton = memo(({ index, total, selected, onClick, children, ...rest }) => (
  <button
    type="button"
    role="tab"
    aria-label={
      index != null && total != null
        ? `الشريحة ${index + 1} من ${total}`
        : undefined
    }
    aria-selected={selected ?? undefined}
    tabIndex={selected ? 0 : -1}
    onClick={onClick}
    {...rest}
  >
    {children}
  </button>
))

DotButton.displayName = 'DotButton'
